import { v4 as uuid } from 'uuid';

import { BaseStreaming } from '../Streaming';
import StreamingError, {
  StreamingErrorCodes,
} from '../../abstract/StreamingError';
import {
  DoryMessageType,
  DoryServiceType,
} from '../../abstract/StreamingMessages';
import StreamingService from '../../abstract/StreamingService';
import {
  getNewsUnsubscribeRequest,
  NewsRequestPayload,
  NewsSubscribeRequest,
  NewsUnsubscribeResponse,
} from './Messages';
import NewsSubscription, {
  NewsOnAdd,
  NewsOnChange,
  NewsOnDelete,
  NewsOnSnapshot,
  NewsOnSubscribe,
} from './NewsSubscription';

export class NewsStreaming extends StreamingService<DoryServiceType.NEWS> {
  private available = true;

  public get isAvailable(): boolean {
    return this.available;
  }
  /**
   * @hidden
   */
  public set isAvailable(val: boolean) {
    if (val && !this.available) {
      this.available = val;
      this.resubscribe();
    } else {
      this.available = val;
    }
  }

  private subscriptions: {
    [key: string]: NewsSubscription;
  } = {};

  /**
   * @hidden
   */
  constructor(socket: BaseStreaming) {
    super(DoryServiceType.NEWS, 'news', socket);
  }

  /**
   * Subscribe to websocket updates
   *
   * ```js example
   * const fids = [
   *   'id', // required!
   *   'date',
   *   'time',
   *   'language',
   *   'headline',
   *   'instruments',
   *   'nameditems',
   *   'productcodes',
   *   'topiccodes',
   *   'permissions',
   *   'jtime'
   * ];
   *
   * function confirmCallback(d, e) {
   * 	console.log('subscription confirmed', JSON.stringify(!e ? d : e));
   * }
   * function addCallback(d, e) {
   * 	console.log('add', JSON.stringify(!e ? d : e));
   * }
   * function changeCallback(d, e) {
   * 	console.log('change', JSON.stringify(!e ? d : e));
   * }
   * function snapshotCallback(d, e) {
   * 	console.log('snapshot', JSON.stringify(!e ? d : e));
   * }
   * function deleteCallback(d, e) {
   * 	console.log('delete', JSON.stringify(!e ? d : e));
   * }
   *
   * const id = window.dory.ServiceStreaming.News.subscribe(
   *   {
   *     fids,
   *     timezone: 'Europe/Berlin',
   *     tonumber: 15,
   *     history: 'all',
   *     encoding: 'utf-8',
   *     filter: 'ex=deleted fd=2017-12-23T23:59:59'
   *   },
   *   confirmCallback,
   *   addCallback,
   *   changeCallback,
   *   snapshotCallback,
   *   deleteCallback
   * );
   * ```
   *
   * @param data  <code>{ fids: string[], timezone: string, history: string, fromnumber: number, tonumber: number, filter: string }</code>
   * @param subscribe  Executed when subscribe response is received
   * @param add  Contains news article(s) that should be added to the news list
   * @param change  Contains news article(s) that should replace other news article(s), article can be identified by their id
   * @param snapshot  Contains a list of news, replaces news list
   * @param remove  Identifies news article(s), that should be removed from the news list
   * @param id  Unique subscription identifier
   * @returns Unique subscription identifier
   */
  public subscribe(
    data: NewsRequestPayload,
    subscribe: NewsOnSubscribe,
    add: NewsOnAdd,
    change: NewsOnChange,
    snapshot: NewsOnSnapshot,
    remove: NewsOnDelete,
    id: string = uuid()
  ): string {
    const source = 'Streaming.News.subscribe';
    const missingParams =
      subscribe === undefined ||
      add === undefined ||
      change === undefined ||
      snapshot === undefined ||
      remove === undefined
        ? 'Missing required callback, "subscribe" requires callbacks for: \n- subscribe(data, error)\n- add(data, error)\n- change(data, error)\n- snapshot(data, error)\n- remove(data, error)'
        : '';

    const errorCallback = this.getSubscribeErrorHandler(
      source,
      subscribe,
      id,
      this.subscriptions[id] !== undefined,
      missingParams
    );
    if (errorCallback) {
      return undefined;
    } else {
      // Create subscription object
      const subscription = new NewsSubscription(
        id,
        this.SOCKET.EventBus,
        data.fids,
        data.timezone,
        data.history,
        data.fromnumber,
        data.tonumber,
        data.filter,
        subscribe,
        snapshot,
        add,
        change,
        remove
      );
      // Store subscription
      this.subscriptions[id] = subscription;
      // Setup subscription
      subscription.start(this.SOCKET.Timeout);
      // Start subscription
      this.SOCKET.send<NewsSubscribeRequest>(subscription.SubscribeRequest);
    }

    return id;
  }

  /**
   * Unsubscribe from websocket updates
   * @param id  subscription identifier
   * @param callback  Executed when unsubscribe response is received
   */
  public unsubscribe(
    id: string,
    callback?: (e: NewsUnsubscribeResponse, err?: StreamingError) => void
  ): void {
    const source = 'Streaming.News.unsubscribe';
    const subscription = this.subscriptions[id];

    if (!this.SOCKET.isConnected) {
      this.RaiseNotConnectedError(source, callback);
    } else if (!subscription) {
      if (callback) {
        callback(
          undefined,
          new StreamingError(
            source,
            StreamingErrorCodes.INVALID_ID,
            `No subscription for ID ${id} registered!`
          )
        );
      }
    } else {
      // Remove subscription from storage
      const message = getNewsUnsubscribeRequest(id);
      subscription.stop();
      delete this.subscriptions[id];

      if (callback) {
        this.SOCKET.EventBus.once(
          DoryServiceType.NEWS,
          DoryMessageType.UNSUBSCRIBE,
          this.setTimeout(source, this.SOCKET.Timeout, callback)
        );
      }

      this.SOCKET.send(message);
    }
  }

  /**
   * Unsubscribe from all subscriptions
   */
  public unsubscribeAll(): void {
    const ids = Object.keys(this.subscriptions);
    ids.forEach((id) => {
      this.unsubscribe(id);
    });
  }

  /**
   * @hidden
   * Resubscribe to all previous subscriptions
   */
  public resubscribe(): void {
    Object.keys(this.subscriptions).forEach((id) => {
      const subscription = this.subscriptions[id];
      subscription.restart(this.SOCKET.Timeout);
      this.SOCKET.send<NewsSubscribeRequest>(subscription.SubscribeRequest);
    });
  }

  /**
   * @hidden
   * Clear all stored subscription ids
   */
  public clearSubscriptions(): void {
    this.subscriptions = {};
  }
}
